import { useQuery, useMutation, useQueries, useQueryClient } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"

/** ------------------- **/
/*  Products
/** ------------------- **/

export type ProductsPayload = {
  product_id: string
  company_id: string
  product_title?: string
  price?: number
  product_type?: string
  product_url?: string
  velocity?: number
  product_image?: string | null
  colors?: string
  gender?: string
}[]

export function useProduct({
  companyId,
  productId,
}: {
  companyId?: string
  productId?: string
}) {
  const cacheKey = ["product", companyId, productId]
  const query = () => apiRequest(`products/${companyId}/${productId}`)
  return useQuery(cacheKey, query, { enabled: !!companyId && !!productId })
}

export function useSavedProductCollections() {
  const cacheKey = ["savedProductCollections"]
  const query = () => apiRequest(`products/research/collections`)
  return useQuery(cacheKey, query)
}

export function useSavedProductCollection({
  collectionId,
  startDate,
  endDate,
}: {
  collectionId: string
  startDate: string
  endDate: string
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = ["savedProductCollection", collectionId, startDate, endDate]
  const query = () =>
    apiRequest(`products/research/collections/${collectionId}?${paramsString}`)
  return useQuery(cacheKey, query, {
    enabled: !!collectionId,
  })
}

export function useDeleteSavedProductCollection() {
  const queryClient = useQueryClient()

  return useMutation(
    ({ collectionId }: { collectionId: string }) => {
      return apiRequest(
        `products/research/collections?collection_id=${collectionId}`,
        "DELETE"
      )
    },
    {
      onSuccess: () => {
        toast.success("Collection has been successfully deleted.")
        queryClient.refetchQueries(["savedProductCollection"])
        queryClient.refetchQueries(["savedProductCollections"])
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue deleting your collection. Try again later."
        )
      },
    }
  )
}

export function useRemoveProductsSavedProductCollection() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      collectionId,
      products,
    }: {
      collectionId: string
      products: ProductsPayload
    }) => {
      return apiRequest(
        `products/research/collections/${collectionId}`,
        "PUT",
        { products }
      )
    },
    {
      onSuccess: () => {
        toast.success("Products successfully removed from collection.")
        queryClient.refetchQueries(["savedProductCollection"])
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue removing these products. Try again later."
        )
      },
    }
  )
}

export function useCreateNewSavedProductCollection() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      collectionName,
      products,
    }: {
      collectionName: string
      products: ProductsPayload
    }) => {
      return apiRequest(`products/research/collections`, "POST", {
        collection_name: collectionName,
        products: products,
      })
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(["savedProductCollections"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue creating this collection.")
      },
    }
  )
}

export function useAddProductsToCollection() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      collectionId,
      products,
    }: {
      collectionId: string
      products: ProductsPayload
    }) => {
      return apiRequest(
        `products/research/collections?collection_id=${collectionId}`,
        "PUT",
        {
          products: products,
        }
      )
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(["savedProductCollections"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue creating this collection.")
      },
    }
  )
}

export function useProductResearch({
  debouncedSearchTerm = "",
  colors,
  startDate,
  endDate,
  genders,
  limit = 100,
  productTypes = "",
  sizes = "",
  minPrice = 0,
  maxPrice = 1000000000,
  minRev = 0,
  maxRev = 10000000000,
  minVol = 0,
  maxVol = 1000000000,
  filtersOnly,
  ids = "",
}: {
  debouncedSearchTerm?: string | null
  colors?: string | null
  startDate?: string
  endDate?: string
  genders?: string | null
  limit?: number | null
  productTypes?: string | null
  sizes?: string | null
  minPrice?: number | null
  maxPrice?: number | null
  minRev?: number | null
  maxRev?: number | null
  minVol?: number | null
  maxVol?: number | null
  filtersOnly?: boolean
  ids?: string | []
}) {
  const paramsString = qs.stringify({
    search: debouncedSearchTerm ?? undefined,
    colors: colors ?? undefined,
    start_date: startDate ?? undefined,
    end_date: endDate ?? undefined,
    genders: genders ?? undefined,
    limit: limit ?? undefined,
    product_types: productTypes ?? undefined,
    sizes: sizes ?? undefined,
    min_price: minPrice ?? undefined,
    max_price: maxPrice ?? undefined,
    min_rev: minRev ?? undefined,
    max_rev: maxRev ?? undefined,
    min_vol: minVol ?? undefined,
    max_vol: maxVol ?? undefined,
    filters_only: filtersOnly,
    ids,
  })
  const cacheKey = ["productResearch", paramsString]
  const query = () => apiRequest(`products/research?${paramsString}`)
  return useQuery(cacheKey, query)
}

export function useMultipleProductDetails({
  competitorProductAndCompanyIds,
}: {
  competitorProductAndCompanyIds: any
}) {
  const useQueriesArray =
    competitorProductAndCompanyIds?.map((productAndCompanyIds: any) => ({
      queryKey: ["product", productAndCompanyIds[0], productAndCompanyIds[1]],
      queryFn: () =>
        apiRequest(
          `products/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}`
        ),
    })) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useProductSales({
  companyId,
  productId,
  startDate,
  endDate,
}: {
  companyId: string
  productId: string
  startDate: string
  endDate: string
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = ["productSales", companyId, productId, startDate, endDate]
  const query = () =>
    apiRequest(`products/${companyId}/${productId}/sales?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!companyId && !!productId })
}

export function useMultipleProductSales({
  competitorProductAndCompanyIds,
  startDate,
  endDate,
}: {
  competitorProductAndCompanyIds: any
  startDate: string
  endDate: string
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const useQueriesArray =
    competitorProductAndCompanyIds?.map((productAndCompanyIds: any) => ({
      queryKey: [
        "productSales",
        productAndCompanyIds[0],
        productAndCompanyIds[1],
        startDate,
        endDate,
      ],
      queryFn: () =>
        apiRequest(
          `products/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}/sales?${paramsString}`
        ),
    })) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useProductVerticals({
  debouncedSearchTerm,
}: {
  debouncedSearchTerm: string
}) {
  const paramString = qs.stringify({
    search_term: debouncedSearchTerm,
  })

  const cacheKey = ["verticals", debouncedSearchTerm]
  const query = () => apiRequest(`products/verticals?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: debouncedSearchTerm?.length > 2,
  })
}

export function useProductSearch({
  companyId,
  debouncedSearchTerm,
  pageSize,
  pageOffset,
}: {
  companyId: string
  debouncedSearchTerm: string
  pageSize?: number
  pageOffset?: number
}) {
  const paramString = qs.stringify({
    title: debouncedSearchTerm,
    company_id: companyId,
    page_size: pageSize,
    page_number: pageOffset,
  })
  const cacheKey = [
    "searchProducts",
    companyId,
    debouncedSearchTerm,
    pageSize,
    pageOffset,
  ]
  const query = () => apiRequest(`products/search?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId && debouncedSearchTerm?.length > 2,
  })
}

export function useRecentProductEvents({ companyId }: { companyId: string }) {
  const cacheKey = ["recentProductEvents", companyId]
  const query = () => apiRequest(`companies/${companyId}/recent_events`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId,
  })
}

export function useSuggestedProducts({ productId }: { productId: string }) {
  const paramString = qs.stringify({
    product_id: productId,
  })
  const cacheKey = ["suggestedProducts", productId]
  const query = () => apiRequest(`products/competitors?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!productId,
  })
}
